import { CURRENCIES, DOCUMENT_TYPES, isValidNuit, normalizeNuit, parseMoney, PAYMENT_METHODS, SCALE, toScale } from '@makila/fiscal';
import { z } from 'zod';
/**
 * Esquemas de entrada partilhados pela API e pelo browser. Valores
 * monetários entram como texto ou número e passam SEMPRE por parseMoney()
 * — a única implementação (ver packages/fiscal/src/money.ts).
 */
export const moneyInput = z
    .union([z.string(), z.number()])
    .transform((v, ctx) => {
    const r = parseMoney(v);
    if (r === null) {
        ctx.addIssue({ code: 'custom', message: 'Valor inválido' });
        return z.NEVER;
    }
    return r;
});
const nonNegMoney = moneyInput.refine((v) => !v.startsWith('-'), 'Não pode ser negativo');
const pct = moneyInput.refine((v) => Number(v) >= 0 && Number(v) <= 100, 'Percentagem entre 0 e 100');
/**
 * Valores que entram em cálculos de documentos, compras e stock: arredondados
 * à escala com que a BD os grava ANTES de qualquer validação ou cálculo, para
 * que o documento emitido recalcule igual a partir do que fica gravado (P-17).
 */
const scaled = (places) => moneyInput.transform((v) => toScale(v, places));
const qtyIn = scaled(SCALE.quantity);
const priceIn = scaled(SCALE.price).refine((v) => !v.startsWith('-'), 'Não pode ser negativo');
const amountIn = scaled(SCALE.money).refine((v) => !v.startsWith('-'), 'Não pode ser negativo');
/** Pagamentos e liquidações: 0 MT não paga nada (P-20) — recusado com mensagem clara, não pelo erro da BD. */
const paidIn = scaled(SCALE.money).refine((v) => Number(v) > 0, 'O valor tem de ser maior que zero');
const pctIn = scaled(SCALE.pct).refine((v) => Number(v) >= 0 && Number(v) <= 100, 'Percentagem entre 0 e 100');
/**
 * Cópia (Cc) com um ou mais emails separados por ";" ou "," (como no Outlook).
 * Devolve-os normalizados "a@x, b@y" (o formato do nodemailer) ou null.
 */
export const MAX_CC = 10;
export const emailListInput = z.string().max(2000).optional().nullable().transform((v, ctx) => {
    const list = [...new Set((v ?? '').split(/[;,\s]+/).map((x) => x.trim().toLowerCase()).filter(Boolean))];
    const bad = list.filter((x) => !z.email().safeParse(x).success);
    if (bad.length) {
        ctx.addIssue({ code: 'custom', message: `Email inválido em Cc: ${bad.join(', ')}` });
        return z.NEVER;
    }
    if (list.length > MAX_CC) {
        ctx.addIssue({ code: 'custom', message: `No máximo ${MAX_CC} emails em Cc` });
        return z.NEVER;
    }
    return list.length ? list.join(', ') : null;
});
export const nuitInput = z
    .string()
    .transform((v) => normalizeNuit(v))
    .refine((v) => v === null || isValidNuit(v), 'O NUIT tem 9 dígitos');
const optText = (max = 255) => z.string().trim().max(max).optional().nullable().transform((v) => (v ? v : null));
const date = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data inválida');
/** Taxas de IVA admitidas (CIVA art. 17): normal, reduzida, isento. */
const vatRate = z.coerce.number().refine((v) => [0, 5, 16].includes(v), 'Taxa de IVA: 16%, 5% ou isento');
export const passwordSchema = z.string().min(10, 'Mínimo 10 caracteres').max(200);
/** Rótulos reservados: serviços do domínio e caminhos que não podem ser empresa. */
export const RESERVED_SLUGS = new Set([
    'www', 'api', 'app', 'admin', 'mail', 'webmail', 'cpanel', 'autodiscover', 'autoconfig',
    'smtp', 'imap', 'pop', 'ftp', 'static', 'assets', 'cdn', 'help', 'support', 'status',
    'blog', 'public', 'platform', 'makila', 'v', 'ns1', 'ns2', 'mx', 'test', 'dev', 'staging',
    // Makila POS (docs/09): pos.<domínio> é a aplicação do POS, não uma empresa.
    'pos',
]);
/** Normaliza um texto para um slug de subdomínio (minúsculas, sem acentos). */
export const slugify = (s) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
/** Sugestão de endereço a partir do nome: a primeira palavra significativa
 *  (ex.: "Ribeiro Tecnologias & Serviços, Lda" → "ribeiro"). */
export const suggestSlug = (name) => (slugify(name).split('-')[0] || 'empresa').slice(0, 32);
/** Endereço do subdomínio da empresa: <slug>.makila.sistemamz.com */
export const slugSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(2, 'Mínimo 2 caracteres')
    .max(32, 'Máximo 32 caracteres')
    .regex(/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/, 'Só letras, números e hífen (não no início nem no fim)')
    .refine((s) => !s.includes('--'), 'Sem hífens seguidos')
    .refine((s) => !RESERVED_SLUGS.has(s), 'Este endereço está reservado');
export const signupSchema = z.object({
    name: z.string().trim().min(2).max(120),
    email: z.email().max(200).transform((v) => v.toLowerCase()),
    password: passwordSchema,
    phone: optText(30),
    company: z.object({
        legalName: z.string().trim().min(2).max(200),
        // Opcional na API (fallback a partir do nome); o formulário exige-o sempre.
        slug: slugSchema.optional(),
        nuit: nuitInput.refine((v) => v !== null, 'NUIT obrigatório'),
        address: z.string().trim().min(2).max(255),
        city: z.string().trim().min(2).max(100),
        vatRegime: z.enum(['normal', 'simplified', 'exempt', 'ispc']).default('normal'),
    }),
    acceptTerms: z.literal(true, { message: 'É necessário aceitar os termos' }),
});
export const loginSchema = z.object({
    email: z.email().transform((v) => v.toLowerCase()),
    password: z.string().min(1).max(200),
    totp: z.string().regex(/^\d{6}$/).optional(),
    /** Código de recuperação (uso único), em alternativa ao TOTP. */
    recoveryCode: z.string().trim().min(6).max(20).optional(),
    tenantId: z.uuid().optional(),
});
const optEmail = z.email('Email inválido').optional().nullable().or(z.literal('').transform(() => null));
export const customerSchema = z.object({
    customerType: z.enum(['company', 'individual']).default('company'),
    code: optText(30),
    name: z.string().trim().min(1, 'Nome obrigatório').max(200),
    tradeName: optText(200),
    nuit: nuitInput.optional().nullable(),
    isFinalConsumer: z.boolean().default(false),
    isPublicEntity: z.boolean().default(false),
    address: z.string().trim().max(255).default(''),
    city: z.string().trim().max(100).default(''),
    province: optText(100),
    postalCode: optText(20),
    deliveryAddress: optText(255),
    deliveryCity: optText(100),
    email: optEmail,
    billingEmail: optEmail,
    phone: optText(30),
    mobile: optText(30),
    whatsapp: optText(30),
    website: optText(200),
    contactName: optText(120),
    contactRole: optText(80),
    paymentTermsDays: z.coerce.number().int().min(0).max(365).optional().nullable(),
    defaultPaymentMethod: z.enum(Object.keys(PAYMENT_METHODS)).optional().nullable(),
    defaultDiscountPct: pct.optional().nullable(),
    creditLimit: nonNegMoney.optional().nullable(),
    creditDaysLimit: z.coerce.number().int().min(0).max(3650).optional().nullable(),
    withholdingPct: pct.optional().nullable(),
    withholdingType: z.enum(['IRPS', 'IRPC']).optional().nullable(),
    tags: z.array(z.string().trim().min(1).max(30)).max(10).default([]),
    notes: optText(2000),
    /** Recebe lembretes automáticos (cobrança e seguimento de pró-formas). */
    autoReminders: z.boolean().default(true),
    active: z.boolean().default(true),
});
export const productSchema = z
    .object({
    code: z.string().trim().min(1).max(40),
    name: z.string().trim().min(1).max(200),
    description: optText(2000),
    kind: z.enum(['P', 'S', 'O']).default('S'),
    unit: z.string().trim().min(1).max(15).default('un'),
    barcode: optText(40),
    unitPrice: nonNegMoney,
    priceIncludesTax: z.boolean().default(false),
    taxRate: vatRate,
    exemptionCode: optText(10),
    costPrice: nonNegMoney.default('0'),
    price2: nonNegMoney.optional().nullable(),
    categoryId: z.uuid().optional().nullable(),
    supplierId: z.uuid().optional().nullable(),
    supplierRef: optText(60),
    trackStock: z.boolean().default(false),
    minStock: moneyInput.optional().nullable(),
    maxStock: moneyInput.optional().nullable(),
    defaultWarehouseId: z.uuid().optional().nullable(),
    active: z.boolean().default(true),
})
    .refine((p) => p.taxRate > 0 || !!p.exemptionCode, { path: ['exemptionCode'], message: 'Artigo isento exige motivo de isenção (CIVA art. 27 n.º 5 e)' });
export const documentLineSchema = z
    .object({
    productId: z.uuid().optional().nullable(),
    productCode: z.string().trim().max(40).optional().nullable(),
    description: z.string().trim().min(1, 'Descrição obrigatória').max(500),
    note: optText(500),
    quantity: qtyIn.refine((v) => Number(v) > 0, 'Quantidade tem de ser positiva'),
    unit: z.string().trim().max(15).default('un'),
    unitPrice: priceIn,
    priceIncludesTax: z.boolean().default(false),
    discountPct: pctIn.default('0'),
    taxRate: vatRate,
    exemptionCode: optText(10),
    exemptionReason: optText(255),
})
    .refine((l) => l.taxRate > 0 || !!l.exemptionCode, { path: ['exemptionCode'], message: 'Linha isenta exige motivo' });
export const documentPaymentSchema = z.object({
    method: z.enum(Object.keys(PAYMENT_METHODS)),
    amount: paidIn,
    reference: optText(100),
    paidAt: date.optional(),
});
export const documentDraftSchema = z.object({
    docType: z.enum(DOCUMENT_TYPES),
    customerId: z.uuid(),
    issueDate: date.optional().nullable(),
    dueDate: date.optional().nullable(),
    originDocumentId: z.uuid().optional().nullable(),
    reason: optText(500),
    notes: optText(2000),
    customerReference: optText(100),
    globalDiscountPct: pctIn.default('0'),
    /**
     * Moeda do documento (P-01): noutra moeda que não o metical, os preços e os
     * portes vêm nessa moeda e o servidor converte-os para MZN ao câmbio indicado
     * (MT por 1 unidade da moeda). Em MZN o câmbio é sempre 1.
     */
    currencyCode: z.enum(Object.keys(CURRENCIES)).default('MZN'),
    exchangeRate: scaled(SCALE.rate).refine((v) => Number(v) > 0, 'O câmbio tem de ser maior que zero').default('1'),
    shippingAmount: amountIn.default('0'),
    // Os portes seguem as mesmas taxas das linhas: sem isto entrava qualquer número (até negativo) no IVA assinado.
    shippingTaxRate: vatRate.default(16),
    withholdingPct: pctIn.default('0'),
    withholdingType: z.enum(['IRPS', 'IRPC']).optional().nullable(),
    /** Omisso: os documentos movimentam stock, excepto a NC, que só repõe stock quando se indica (P-12). */
    movesStock: z.boolean().optional(),
    salespersonId: z.uuid().optional().nullable(),
    /** Forma de pagamento prevista (FT/PF) — FR/VD/RE registam o pagamento em `payments`. */
    paymentMethod: z.enum(Object.keys(PAYMENT_METHODS)).optional().nullable(),
    /** Condição de pagamento em dias (0 = pronto pagamento); null com dueDate = data acordada. */
    paymentTermsDays: z.coerce.number().int().min(0).max(365).optional().nullable(),
    internalNotes: optText(2000),
    deliveryAddress: optText(255),
    loadPlace: optText(255),
    unloadPlace: optText(255),
    transportStart: z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/, 'Data/hora inválida').optional().nullable(),
    vehiclePlate: optText(20),
    lines: z.array(documentLineSchema).max(500).default([]),
    payments: z.array(documentPaymentSchema).max(10).default([]),
    allocations: z.array(z.object({ invoiceId: z.uuid(), amount: paidIn })).max(200).default([]),
    /** FR de adiantamento (P-21): recebimento antecipado, com IVA, a deduzir numa factura posterior. */
    isAdvance: z.boolean().default(false),
    /** Factura final (FT): adiantamentos a deduzir, em MT (valor com IVA). */
    advances: z.array(z.object({ documentId: z.uuid(), amount: paidIn })).max(20).default([]),
});
export const issueOptionsSchema = z.object({
    overrideCreditLimit: z.boolean().default(false),
    overrideDiscount: z.boolean().default(false),
});
// ── Automação ───────────────────────────────────────────────────────────────
const dayList = (min, max, maxLen) => z.array(z.coerce.number().int().min(min).max(max))
    .min(1, 'Indique pelo menos um dia').max(maxLen, `No máximo ${maxLen} dias`)
    .transform((a) => [...new Set(a)].sort((x, y) => x - y));
export const automationSettingsSchema = z.object({
    autoDunning: z.boolean(),
    /** Dias relativos ao vencimento: −3 = três dias antes, 0 = no dia, 7 = uma semana depois. */
    dunningDays: dayList(-30, 180, 8),
    quoteFollowup: z.boolean(),
    /** Dias depois de a pró-forma ser enviada ao cliente. */
    quoteFollowupDays: dayList(1, 90, 5),
    digestFrequency: z.enum(['off', 'daily', 'weekly']),
    stockAlerts: z.boolean(),
});
export const RECURRING_INTERVALS = { 1: 'Mensal', 2: 'Bimestral', 3: 'Trimestral', 6: 'Semestral', 12: 'Anual' };
export const RECURRING_MODES = {
    draft: 'Preparar rascunho (eu emito)',
    issue: 'Emitir automaticamente',
    issue_send: 'Emitir e enviar por email',
};
const recurringBase = z.object({
    name: z.string().trim().min(2, 'Dê um nome à avença').max(120),
    intervalMonths: z.coerce.number().int().refine((v) => [1, 2, 3, 6, 12].includes(v), 'Periodicidade inválida'),
    nextRun: date,
    endDate: date.optional().nullable(),
    mode: z.enum(['draft', 'issue', 'issue_send']),
    sendTo: z.email('Email inválido').optional().nullable().or(z.literal('').transform(() => null)),
    active: z.boolean().default(true),
});
const recurringRefine = (s) => s
    .refine((r) => r.mode !== 'issue_send' || !!r.sendTo, { path: ['sendTo'], message: 'Indique o email para onde enviar' })
    .refine((r) => !r.endDate || r.endDate >= r.nextRun, { path: ['endDate'], message: 'A data de fim é anterior à primeira emissão' });
/** Nova avença a partir de um documento existente (as linhas e condições vêm dele). */
export const recurringCreateSchema = recurringRefine(recurringBase.extend({ documentId: z.uuid() }));
export const recurringUpdateSchema = recurringRefine(recurringBase);
export const voidSchema = z.object({ reason: z.string().trim().min(5, 'Indique o motivo').max(500) });
export const periodSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Período AAAA-MM');
export const listQuerySchema = z.object({
    q: z.string().trim().max(100).optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
// ── Compras e stock ──────────────────────────────────────────────────────────
const paymentMethod = z.enum(Object.keys(PAYMENT_METHODS));
export const supplierSchema = z.object({
    code: optText(30),
    name: z.string().trim().min(1, 'Nome obrigatório').max(200),
    nuit: nuitInput.optional().nullable(),
    category: optText(80),
    address: z.string().trim().max(255).default(''),
    city: z.string().trim().max(100).default(''),
    province: optText(100),
    postalCode: optText(20),
    email: optEmail,
    phone: optText(30),
    mobile: optText(30),
    website: optText(200),
    contactName: optText(120),
    paymentTermsDays: z.coerce.number().int().min(0).max(365).optional().nullable(),
    defaultPaymentMethod: paymentMethod.optional().nullable(),
    bankName: optText(100),
    bankAccount: optText(40),
    nib: z.string().trim().regex(/^\d{21}$/, 'O NIB tem 21 dígitos').optional().nullable().or(z.literal('').transform(() => null)),
    notes: optText(2000),
    active: z.boolean().default(true),
});
export const purchaseSchema = z.object({
    supplierId: z.uuid('Escolha o fornecedor'),
    supplierDocNumber: z.string().trim().min(1, 'Número da factura do fornecedor obrigatório').max(60),
    docDate: date,
    dueDate: date.optional().nullable(),
    movesStock: z.boolean().default(true),
    warehouseId: z.uuid().optional().nullable(),
    notes: optText(2000),
    lines: z.array(z.object({
        productId: z.uuid().optional().nullable(),
        description: z.string().trim().min(1, 'Descrição obrigatória').max(500),
        quantity: qtyIn.refine((v) => Number(v) > 0, 'Quantidade tem de ser positiva'),
        unitCost: priceIn,
        discountPct: pctIn.default('0'),
        taxRate: vatRate,
    })).min(1, 'A factura tem de ter pelo menos uma linha').max(500),
});
export const supplierPaymentSchema = z.object({
    supplierId: z.uuid(),
    paymentDate: date,
    method: paymentMethod,
    reference: optText(100),
    notes: optText(500),
    allocations: z.array(z.object({ purchaseId: z.uuid(), amount: paidIn })).min(1, 'Indique as facturas a pagar').max(200),
});
export const stockMovementSchema = z.object({
    productId: z.uuid(),
    warehouseId: z.uuid().optional().nullable(),
    kind: z.enum(['in', 'out', 'adjust']),
    quantity: qtyIn.refine((v) => Number(v) !== 0, 'Quantidade não pode ser zero'),
    unitCost: priceIn.optional().nullable(),
    note: z.string().trim().min(3, 'Indique o motivo do movimento').max(255),
});
export const warehouseSchema = z.object({ name: z.string().trim().min(2).max(100), isDefault: z.boolean().default(false), active: z.boolean().default(true) });
export const categorySchema = z.object({ name: z.string().trim().min(2).max(100) });
export const inventoryOpenSchema = z.object({ warehouseId: z.uuid(), countDate: date, notes: optText(500), onlyTracked: z.boolean().default(true) });
export const inventoryCountSchema = z.object({ counts: z.array(z.object({ productId: z.uuid(), countedQty: qtyIn.nullable() })).max(5000) });
// ── Caixa e bancos ───────────────────────────────────────────────────────────
export const CASH_MANUAL_KINDS = { deposit: 'Depósito / entrada', withdrawal: 'Levantamento / saída', expense: 'Despesa', opening: 'Saldo inicial' };
export const cashAccountSchema = z.object({
    name: z.string().trim().min(2, 'Mínimo 2 caracteres').max(100),
    kind: z.enum(['cash', 'bank', 'mobile']),
    bankAccountId: z.uuid().optional().nullable(),
    active: z.boolean().default(true),
});
/** Meio de pagamento → conta onde entra (ex.: { NU: <caixa>, TB: <banco> }). */
export const cashMethodsSchema = z.partialRecord(paymentMethod, z.uuid());
export const cashMovementSchema = z.object({
    accountId: z.uuid('Escolha a conta'),
    kind: z.enum(['deposit', 'withdrawal', 'expense', 'opening']),
    amount: paidIn,
    movementDate: date,
    description: z.string().trim().min(3, 'Indique o motivo').max(255),
});
export const cashTransferSchema = z.object({
    fromAccountId: z.uuid('Escolha a conta de origem'),
    toAccountId: z.uuid('Escolha a conta de destino'),
    amount: paidIn,
    movementDate: date,
    description: optText(255),
}).refine((t) => t.fromAccountId !== t.toAccountId, { path: ['toAccountId'], message: 'Escolha uma conta diferente da de origem' });
export const cashClosingSchema = z.object({
    accountId: z.uuid('Escolha a conta'),
    closingDate: date,
    counted: scaled(SCALE.money).refine((v) => !v.startsWith('-'), 'Não pode ser negativo'),
    notes: optText(500),
});
//# sourceMappingURL=schemas.js.map